import { isLegacyAgentActivityWorkCompatible } from "../../agent-work-activity.js";
import { MAX_TEXT_LENGTH } from "./constants.js";
import { trimmedStringFromUnknown } from "./shared.js";
import { openMeldRuntimeProfileIdSchema } from "./profile.js";
import { spaceMentionLabelSchema } from "../http/space-mention-label.js";
import { clientMessageIdSchema } from "./client-message-id.js";
import { spaceAgentActivitySchema } from "./agent-stream.js";
import { executionContextSelectionSchema } from "./execution-context.js";
import { messageEnvelopeSchema } from "./message-envelope.js";
import { spaceMessageFileReferencesSchema } from "../http/space-files.js";
import { spaceWorkHandoffSchema } from "../../space-work-handoff.js";
import { centerAgentInteractionSourceSchema } from "./center-agent-interaction-source.js";
import { coreReadinessFactsChangedSchema } from "./core-readiness-facts-changed.js";
import { MAX_SIGNAL_REPLY_TO_PREVIEW_LENGTH, signalReplyToSchema } from "./signal-reply-to.js";
import { signalStatusDataSchema as signalStatusDataSchema$1 } from "../space-mesh/signal-status.js";
import { signalSystemNoticeDataSchema as signalSystemNoticeDataSchema$1 } from "../space-mesh/signal-system-notice.js";
import { conversationCompactionMarkerSchema } from "./conversation-compaction.js";
import { spaceMessageAttachmentsSchema } from "./space-attachment.js";
import { wakeDeviceWaitSchema } from "./wake-device-wait.js";
import * as z$1 from "zod/v4";
//#region src/schemas/protocol/signal.ts
const MULTI_SPACE_PATTERN = /\s+/g;
const DELETE_CODE_POINT = 127;
const COMPLETE_FENCED_BLOCK_PATTERN = /```[\s\S]*?```/gu;
const TRAILING_OPENMELD_SPACE_ACTION_BLOCK_PATTERN = /```openmeld-space-action[\s\S]*$/u;
const SPACE_MEMBER_JOIN_NOTICE_PURPOSE = "space_membership_join_notice";
const SPACE_MEMBER_REMOVE_NOTICE_PURPOSE = "space_membership_remove_notice";
const SPACE_MENTION_REJECT_NOTICE_PURPOSE = "space_mention_reject_notice";
const AGENT_REPLYING_PURPOSE = "agent_replying";
const AGENT_REPLYING_END_PURPOSE = "agent_replying_end";
const AGENT_REPLY_PREVIEW_PURPOSE = "agent_reply_preview";
const AGENT_REPLY_PREVIEW_END_PURPOSE = "agent_reply_preview_end";
const AGENT_REPLY_FINAL_PURPOSE = "agent_reply_final";
const REMOTE_AGENT_FAILURE_PURPOSE = "remote_agent_failure";
const DISPATCH_ACTIVITY_DISPATCHING_PURPOSE = "dispatch_activity_dispatching";
const DISPATCH_ACTIVITY_RETRYING_PURPOSE = "dispatch_activity_retrying";
const DISPATCH_ACTIVITY_FAILED_PURPOSE = "dispatch_activity_failed";
const CONVERSATION_CONTINUITY_WARNING_PURPOSE = "conversation_continuity_warning";
const sanitizeSignalTextPreview = (text) => {
	const normalized = trimmedStringFromUnknown(text).replace(COMPLETE_FENCED_BLOCK_PATTERN, " ").replace(TRAILING_OPENMELD_SPACE_ACTION_BLOCK_PATTERN, " ");
	if (normalized.length === 0) return null;
	const singleLine = normalized.split("").map((character) => isControlCharacter(character) ? " " : character).join("").replace(MULTI_SPACE_PATTERN, " ").trim();
	if (singleLine.length === 0) return null;
	return singleLine.length <= 120 ? singleLine : `${singleLine.slice(0, 119)}…`;
};
const signalTextReplyToSchema = signalReplyToSchema;
const signalTextContentSchema = z$1.preprocess(trimmedStringFromUnknown, z$1.string().max(MAX_TEXT_LENGTH));
const runtimeDeviceDisplayNameSchema = z$1.preprocess(trimmedStringFromUnknown, z$1.string().min(1).max(80));
const legacySpaceAgentActivitySchema = spaceAgentActivitySchema.superRefine((activity, context) => {
	activity.steps.forEach((step, index) => {
		if (!isLegacyAgentActivityWorkCompatible(step.work)) context.addIssue({
			code: "custom",
			message: "work is not compatible with legacy Agent Activity",
			path: [
				"steps",
				index,
				"work"
			]
		});
	});
});
const signalTextMetaSchema = z$1.object({
	workHandoff: spaceWorkHandoffSchema.optional(),
	threadWakeContinuation: z$1.literal(true).optional(),
	deviceWait: wakeDeviceWaitSchema.optional(),
	restoredFromSignalId: z$1.string().min(1).optional(),
	repostedAt: z$1.number().int().nonnegative().optional(),
	messageRevision: z$1.number().int().nonnegative().optional(),
	editedAt: z$1.number().int().nonnegative().optional(),
	recalledAt: z$1.number().int().nonnegative().optional(),
	changedMessageId: z$1.string().min(1).optional(),
	transient: z$1.literal(true).optional(),
	agentActivity: legacySpaceAgentActivitySchema.optional(),
	agentWorkSummary: spaceAgentActivitySchema.optional(),
	clientMessageId: clientMessageIdSchema.optional(),
	centerAgentExecutionInput: z$1.strictObject({
		dispatchId: z$1.string().min(1).max(200),
		targetProfileId: openMeldRuntimeProfileIdSchema
	}).optional(),
	authorHasRead: z$1.literal(true).optional(),
	centerAgentInteractionSource: centerAgentInteractionSourceSchema.optional(),
	conversationCompaction: conversationCompactionMarkerSchema.optional(),
	sourceEventId: z$1.preprocess(trimmedStringFromUnknown, z$1.string().max(200)).optional(),
	sourceClientMessageId: clientMessageIdSchema.optional(),
	purpose: z$1.preprocess(trimmedStringFromUnknown, z$1.string().min(1).max(80)).optional(),
	errorCode: z$1.preprocess(trimmedStringFromUnknown, z$1.string().min(1).max(120)).optional(),
	dispatchId: z$1.preprocess(trimmedStringFromUnknown, z$1.string().max(200)).optional(),
	targetProfileId: openMeldRuntimeProfileIdSchema.optional(),
	targetProfileKind: z$1.enum(["human", "agent"]).optional(),
	targetProfileName: z$1.preprocess(trimmedStringFromUnknown, z$1.string().min(1).max(64)).optional(),
	targetMentionLabel: spaceMentionLabelSchema.optional(),
	runtimeDeviceDisplayName: runtimeDeviceDisplayNameSchema.optional(),
	coreReadinessFactsChanged: coreReadinessFactsChangedSchema.optional(),
	staleness: z$1.object({ newMessagesDuringComposition: z$1.number().int().positive() }).optional(),
	addedByProfileId: openMeldRuntimeProfileIdSchema.optional(),
	addedByDisplayName: z$1.preprocess(trimmedStringFromUnknown, z$1.string().min(1).max(64)).optional(),
	removedByProfileId: openMeldRuntimeProfileIdSchema.optional(),
	removedByDisplayName: z$1.preprocess(trimmedStringFromUnknown, z$1.string().min(1).max(64)).optional()
});
const validateAuthoredSignalTextMeta = (meta, context) => {
	if (meta.purpose !== "space_membership_join_notice" && meta.purpose !== "space_membership_remove_notice") return;
	if (!meta.targetProfileId) context.addIssue({
		code: "custom",
		message: "targetProfileId is required for space member notices",
		path: ["targetProfileId"]
	});
	if (!meta.targetProfileKind) context.addIssue({
		code: "custom",
		message: "targetProfileKind is required for space member notices",
		path: ["targetProfileKind"]
	});
	if (meta.purpose === "space_membership_join_notice" && !meta.addedByProfileId) context.addIssue({
		code: "custom",
		message: "addedByProfileId is required for space member notices",
		path: ["addedByProfileId"]
	});
	if (meta.purpose === "space_membership_join_notice" && !meta.addedByDisplayName) context.addIssue({
		code: "custom",
		message: "addedByDisplayName is required for space member notices",
		path: ["addedByDisplayName"]
	});
	if (meta.purpose === "space_membership_remove_notice" && !meta.removedByProfileId) context.addIssue({
		code: "custom",
		message: "removedByProfileId is required for space member notices",
		path: ["removedByProfileId"]
	});
	if (meta.purpose === "space_membership_remove_notice" && !meta.removedByDisplayName) context.addIssue({
		code: "custom",
		message: "removedByDisplayName is required for space member notices",
		path: ["removedByDisplayName"]
	});
};
const postedSignalTextMetaSchema = signalTextMetaSchema.omit({
	repostedAt: true,
	messageRevision: true,
	editedAt: true,
	recalledAt: true,
	changedMessageId: true,
	agentActivity: true,
	agentWorkSummary: true,
	conversationCompaction: true
}).extend({ deviceWait: z$1.never({ error: "Set deviceWait only at the top level of the message request." }).optional() }).superRefine(validateAuthoredSignalTextMeta);
const systemEmittedSignalTextMetaSchema = signalTextMetaSchema.omit({
	agentActivity: true,
	agentWorkSummary: true
}).superRefine(validateAuthoredSignalTextMeta);
const addSignalTextMessageEnvelopeIssues = (input, context) => {
	if (!input.messageEnvelope) return;
	if (input.messageEnvelope.text !== input.text) context.addIssue({
		code: z$1.ZodIssueCode.custom,
		path: ["messageEnvelope", "text"],
		message: "messageEnvelope.text must match text"
	});
	if (input.replyTo?.signalId && input.messageEnvelope.replyToSignalId && input.replyTo.signalId !== input.messageEnvelope.replyToSignalId) context.addIssue({
		code: z$1.ZodIssueCode.custom,
		path: ["messageEnvelope", "replyToSignalId"],
		message: "messageEnvelope.replyToSignalId must match replyTo.signalId"
	});
};
const addSignalTextContentIssues = (input, context) => {
	if (input.text.length > 0 || (input.files?.length ?? 0) > 0 || (input.attachments?.length ?? 0) > 0) return;
	context.addIssue({
		code: z$1.ZodIssueCode.custom,
		path: ["text"],
		message: "A message must include text or at least one file"
	});
};
const buildSignalTextDataSchema = (metaSchema) => z$1.strictObject({
	text: signalTextContentSchema,
	deviceWait: wakeDeviceWaitSchema.optional(),
	contextSelection: executionContextSelectionSchema.optional(),
	attachments: spaceMessageAttachmentsSchema.optional(),
	files: spaceMessageFileReferencesSchema.optional(),
	messageEnvelope: messageEnvelopeSchema.optional(),
	replyTo: signalTextReplyToSchema.optional(),
	meta: metaSchema.optional()
}).superRefine(addSignalTextContentIssues).superRefine(addSignalTextMessageEnvelopeIssues);
const signalTextDataSchema = buildSignalTextDataSchema(signalTextMetaSchema);
const signalTextSchema = z$1.object({
	type: z$1.literal("signal.text"),
	data: buildSignalTextDataSchema(postedSignalTextMetaSchema)
});
const systemEmittedSignalTextSchema = z$1.object({
	type: z$1.literal("signal.text"),
	data: buildSignalTextDataSchema(systemEmittedSignalTextMetaSchema)
});
const signalStatusSchema = z$1.strictObject({
	type: z$1.literal("signal.status"),
	data: signalStatusDataSchema$1
});
const signalStatusDataSchema = signalStatusDataSchema$1;
const signalSystemNoticeSchema = z$1.strictObject({
	type: z$1.literal("signal.system_notice"),
	data: signalSystemNoticeDataSchema$1
});
const signalSystemNoticeDataSchema = signalSystemNoticeDataSchema$1;
const isControlCharacter = (character) => {
	const codePoint = character.codePointAt(0);
	return typeof codePoint === "number" && (codePoint < 32 || codePoint === DELETE_CODE_POINT);
};
//#endregion
export { AGENT_REPLYING_END_PURPOSE, AGENT_REPLYING_PURPOSE, AGENT_REPLY_FINAL_PURPOSE, AGENT_REPLY_PREVIEW_END_PURPOSE, AGENT_REPLY_PREVIEW_PURPOSE, CONVERSATION_CONTINUITY_WARNING_PURPOSE, DISPATCH_ACTIVITY_DISPATCHING_PURPOSE, DISPATCH_ACTIVITY_FAILED_PURPOSE, DISPATCH_ACTIVITY_RETRYING_PURPOSE, MAX_SIGNAL_REPLY_TO_PREVIEW_LENGTH, REMOTE_AGENT_FAILURE_PURPOSE, SPACE_MEMBER_JOIN_NOTICE_PURPOSE, SPACE_MEMBER_REMOVE_NOTICE_PURPOSE, SPACE_MENTION_REJECT_NOTICE_PURPOSE, sanitizeSignalTextPreview, signalStatusDataSchema, signalStatusSchema, signalSystemNoticeDataSchema, signalSystemNoticeSchema, signalTextDataSchema, signalTextReplyToSchema, signalTextSchema, systemEmittedSignalTextSchema };

//# sourceMappingURL=signal.js.map